import { useEffect, useState } from "react";
import { ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatApiError, money, money2 } from "@/lib/api";

const inputCls =
  "h-11 w-full rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal";

const emptyQuote = { listing_id: "", name: "", email: "", phone: "", start_date: "", end_date: "", notes: "" };

export const QuotesTab = ({ listings }) => {
  const [pending, setPending] = useState([]);
  const [sent, setSent] = useState([]);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState(emptyQuote);
  const [busy, setBusy] = useState(false);
  const [quoteFilter, setQuoteFilter] = useState("all");

  const visibleSent = sent.filter((q) => quoteFilter === "all" || q.status === quoteFilter);

  const load = () => {
    api.get("/admin/quotes/pending").then((r) => setPending(r.data)).catch(() => {});
    api.get("/admin/quotes").then((r) => setSent(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  };

  useEffect(load, []);
  const quotable = listings.filter((l) => l.billing !== "call");

  const startQuote = (l) => {
    setForm((f) => ({ ...f, name: l.name || "", email: l.email || "", phone: l.phone || "", notes: l.details || "" }));
    document.querySelector('[data-testid="quote-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/quotes", {
        ...form, origin_url: window.location.origin,
      });
      toast.success(`Quote ${data.reference} emailed to ${data.customer.email}`);
      setForm(emptyQuote);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="quotes-tab" className="mt-8 grid gap-8 lg:grid-cols-2">
      <div>
        <h3 className="font-display text-lg font-bold text-ink">June leads</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
          quote requests from the chat assistant
        </p>
        <div className="mt-4 space-y-3" data-testid="leads-list">
          {leads.length === 0 && (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center text-sm text-slate-400">
              No leads from June yet — they'll land here and in your email.
            </p>
          )}
          {leads.map((l) => (
            <div key={l.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`lead-card-${l.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-ink">{l.name}</p>
                <span className="rounded-full bg-royal-light px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-royal">
                  via June
                </span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                {l.email}{l.phone ? ` · ${l.phone}` : ""} · {fmtDate((l.created_at || "").slice(0, 10))}
              </p>
              {l.details && <p className="mt-1.5 rounded-lg bg-paper px-3 py-2 text-xs text-slate-600">{l.details}</p>}
              <button
                onClick={() => startQuote(l)}
                data-testid={`lead-quote-${l.id}`}
                className="mt-3 rounded-full bg-royal px-4 py-2 text-xs font-semibold text-white transition-all duration-200 hover:bg-royal-dark"
              >
                Start their quote
              </button>
            </div>
          ))}
        </div>

        <h3 className="mt-10 font-display text-lg font-bold text-ink">Awaiting payment</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
          started checkout, deposit not paid
        </p>
        <div className="mt-4 space-y-3" data-testid="pending-quotes-list">
          {pending.length === 0 && (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center text-sm text-slate-400">
              No unpaid checkouts — every quote has been settled.
            </p>
          )}
          {pending.map((q) => (
            <div key={q.session_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`pending-quote-${q.session_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-ink">{q.customer_name || q.customer_email}</p>
                <p className="font-display text-base font-extrabold text-royal">{money2(q.amount)} deposit</p>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{q.customer_email}{q.customer_phone ? ` · ${q.customer_phone}` : ""}</p>
              <p className="mt-1 text-xs text-slate-600">
                {q.listing_title} · {fmtDate(q.start_date)} – {fmtDate(q.end_date)}
              </p>
              <div className="mt-3 flex items-center justify-between">
                <span className="rounded-full bg-amber-50 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-700">
                  awaiting payment
                </span>
                {q.checkout_url && (
                  <a href={q.checkout_url} target="_blank" rel="noreferrer"
                     data-testid={`pending-quote-link-${q.session_id}`}
                     className="flex items-center gap-1 text-xs font-semibold text-royal hover:underline">
                    Payment link <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <h3 className="mt-10 font-display text-lg font-bold text-ink">Sent quotes</h3>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="quote-filters">
          {[["all", "All"], ["sent", "Sent"], ["email_failed", "Email failed"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setQuoteFilter(key)}
              data-testid={`quote-filter-${key}`}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                quoteFilter === key ? "bg-ink text-white shadow-md" : "border border-slate-200 bg-white text-slate-500 hover:border-royal hover:text-royal"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="mt-4 space-y-3" data-testid="sent-quotes-list">
          {visibleSent.length === 0 && (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center text-sm text-slate-400">
              No quotes in this view.
            </p>
          )}
          {visibleSent.map((q) => (
            <div key={q.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`sent-quote-${q.id}`}>
              <div>
                <p className="font-mono text-xs font-semibold text-royal">{q.reference}</p>
                <p className="mt-1 font-semibold text-ink">{q.customer.name} <span className="font-normal text-slate-500">· {q.customer.email}</span></p>
                <p className="mt-0.5 text-xs text-slate-500">{q.listing_title} · {fmtDate(q.start_date)} – {fmtDate(q.end_date)}</p>
              </div>
              <div className="text-right">
                <p className="font-display text-base font-extrabold text-ink">{money(q.total)}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">
                  sent {fmtDate((q.created_at || "").slice(0, 10))} · by {q.created_by}
                </p>
                {q.status === "email_failed" && (
                  <span className="mt-1 inline-block rounded-full bg-red-50 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-red-600" data-testid={`quote-failed-${q.id}`}>
                    email failed — call customer
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-display text-lg font-bold text-ink">Send a quote</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
          emailed with totals + a book-online link
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3 rounded-2xl border border-slate-200 bg-white p-6" data-testid="quote-form">
          <select value={form.listing_id} onChange={(e) => setForm({ ...form, listing_id: e.target.value })}
                  data-testid="quote-listing-select" className={inputCls} required>
            <option value="">Pick a package…</option>
            {quotable.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
          </select>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 placeholder="Customer full name *" data-testid="quote-name-input" className={inputCls} required />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   type="email" placeholder="Email *" data-testid="quote-email-input" className={inputCls} required />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   placeholder="Phone" data-testid="quote-phone-input" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Delivery</span>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                     data-testid="quote-start-date" className={inputCls} required />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Pickup</span>
              <input type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                     data-testid="quote-end-date" className={inputCls} required />
            </label>
          </div>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Note to customer (optional)" rows={2} data-testid="quote-notes-input"
                    className={`${inputCls} resize-none py-3`} />
          <button type="submit" disabled={busy}
                  data-testid="quote-send-button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-50">
            <Send className="h-4 w-4" /> {busy ? "Sending…" : "Email quote"}
          </button>
        </form>
      </div>
    </div>
  );
};
